import { SubmitError, OnSubmitError } from '@wix/checkout-components';
import {
  CheckoutErrorType,
  PaymentCycle,
  PaymentCycle_BIMap,
} from '../../../../utils';
import {
  BiReporter,
  CheckoutBiLogParams,
  FedopsReporter,
  Product,
  SentryReporter,
} from '../../../../types';
import { useCheckoutStore } from '../../../../providers';

export interface UseOnSubmitErrorOptions {
  currentProduct: Product;
  currentCycle: PaymentCycle;
  removeUnloadListener: () => void;
  endSubmitOrderPageInteraction: FedopsReporter;
  logError: SentryReporter;
  biReporters: {
    [CheckoutErrorType.SUBMIT_VALIDATION_ERROR]: BiReporter<CheckoutBiLogParams>;
    [CheckoutErrorType.SUBMIT_BUSINESS_ERROR]: BiReporter<CheckoutBiLogParams>;
    [CheckoutErrorType.SUBMIT_FATAL_ERROR]: BiReporter<CheckoutBiLogParams>;
  };
}

export function useOnSubmitError(
  options?: UseOnSubmitErrorOptions,
): OnSubmitError | undefined {
  const { state } = useCheckoutStore();

  if (options) {
    const { removeUnloadListener } = options;
    return (submitError: SubmitError) => {
      const { error, failureReason } = submitError;
      const additionalInfo = error?.message || failureReason;
      const biParams = {
        productId: options.currentProduct.id,
        productGuid: options.currentProduct.guid,
        eventAggregator: state.orderPageEventAggregator,
        cycle: PaymentCycle_BIMap[options.currentCycle] ?? 0,
        additionalInfo,
      };
      const biReporter = options.biReporters[failureReason];
      removeUnloadListener && removeUnloadListener();
      switch (failureReason) {
        case CheckoutErrorType.SUBMIT_VALIDATION_ERROR:
          biReporter(biParams);
          break;
        case CheckoutErrorType.SUBMIT_BUSINESS_ERROR:
          biReporter(biParams);
          options.endSubmitOrderPageInteraction();
          break;
        case CheckoutErrorType.SUBMIT_FATAL_ERROR:
          biReporter(biParams);
          options.endSubmitOrderPageInteraction();
          options.logError(error);
          break;
        default:
          options.logError(error || failureReason);
      }
    };
  }

  return undefined;
}
